import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../../utils/api';
import { CompanyDetail, BankAccount } from '../../types';

const input: React.CSSProperties = {
  width: '100%', padding: '9px 12px', fontSize: 13, borderRadius: 'var(--radius-md)',
  border: '1px solid var(--border-strong)', background: 'var(--c-gray-50)', color: 'var(--text-primary)', outline: 'none',
};
const lbl: React.CSSProperties = { fontSize: 11, fontWeight: 600, color: 'var(--text-secondary)', display: 'block', marginBottom: 4 };
const card: React.CSSProperties = { background: 'var(--bg-surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius-xl)', padding: 20, marginBottom: 16 };
const btn = (bg: string, color = '#fff'): React.CSSProperties => ({ padding: '8px 14px', borderRadius: 'var(--radius-md)', border: 'none', background: bg, color, fontSize: 13, fontWeight: 600, cursor: 'pointer' });

const readImage = (file: File, cb: (d: string) => void) => { const r = new FileReader(); r.onload = () => cb(String(r.result)); r.readAsDataURL(file); };

// ── Bank account add/edit modal ─────────────────────────────────────────────
function BankForm({ companyId, account, onClose, onSaved }: { companyId: string; account: BankAccount | null; onClose: () => void; onSaved: () => void }) {
  const { t } = useTranslation();
  const isNew = !account;
  const [accountType, setAccountType] = useState<'corporate' | 'personal'>(account?.account_type || 'corporate');
  const [accountName, setAccountName] = useState(account?.account_name || '');
  const [accountNumber, setAccountNumber] = useState(account?.account_number || '');
  const [swiftCode, setSwiftCode] = useState(account?.swift_code || '');
  const [bankAddress, setBankAddress] = useState(account?.bank_address || '');
  const [qrCode, setQrCode] = useState(account?.qr_code || '');
  const [remark, setRemark] = useState(account?.remark || '');
  const [err, setErr] = useState(''); const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true); setErr('');
    const data = { accountType, accountName, accountNumber, swiftCode, bankAddress, qrCode, remark };
    try {
      if (isNew) await api.adminAddBankAccount(companyId, data);
      else await api.adminUpdateBankAccount(companyId, account!.id, data);
      onSaved(); onClose();
    } catch (e: any) { setErr(e.message); setSaving(false); }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.35)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }} onClick={onClose}>
      <div onClick={e => e.stopPropagation()} style={{ width: 520, maxHeight: '90vh', overflowY: 'auto', background: 'var(--bg-surface)', borderRadius: 'var(--radius-xl)', border: '1px solid var(--border)', padding: 24, boxShadow: 'var(--shadow-md)' }}>
        <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 16 }}>{isNew ? t('cs_add_bank') : t('edit')}</div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div>
            <label style={lbl}>{t('cs_acct_type')}</label>
            <div style={{ display: 'flex', gap: 8 }}>
              {(['corporate', 'personal'] as const).map(v => (
                <button key={v} onClick={() => setAccountType(v)} style={{ flex: 1, padding: '8px', borderRadius: 'var(--radius-md)', border: '1px solid', borderColor: accountType === v ? 'var(--accent)' : 'var(--border-strong)', background: accountType === v ? 'var(--accent)' : 'transparent', color: accountType === v ? '#fff' : 'var(--text-secondary)', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
                  {t(v === 'corporate' ? 'cs_corporate' : 'cs_personal')}
                </button>
              ))}
            </div>
          </div>
          <div><label style={lbl}>{t('cs_account_name')}</label><input value={accountName} onChange={e => setAccountName(e.target.value)} style={input} /></div>
          <div><label style={lbl}>{t('cs_account_number')}</label><input value={accountNumber} onChange={e => setAccountNumber(e.target.value)} style={input} /></div>
          <div style={{ display: 'flex', gap: 12 }}>
            <div style={{ flex: 1 }}><label style={lbl}>{t('cs_swift')}</label><input value={swiftCode} onChange={e => setSwiftCode(e.target.value)} style={input} /></div>
          </div>
          <div><label style={lbl}>{t('cs_bank_address')}</label><input value={bankAddress} onChange={e => setBankAddress(e.target.value)} style={input} /></div>
          <div>
            <label style={lbl}>{t('cs_qr')}</label>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              {qrCode
                ? <img src={qrCode} alt="QR" style={{ width: 72, height: 72, objectFit: 'contain', borderRadius: 8, border: '1px solid var(--border)', background: '#fff' }} />
                : <div style={{ width: 72, height: 72, borderRadius: 8, border: '1px dashed var(--border-strong)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-tertiary)' }}><i className="ti ti-qrcode" style={{ fontSize: 22 }} /></div>}
              <label style={{ ...btn('var(--c-gray-100)', 'var(--text-secondary)'), display: 'inline-block' }}>
                <input type="file" accept="image/*" style={{ display: 'none' }} onChange={e => { const f = e.target.files?.[0]; if (f) readImage(f, setQrCode); }} />
                {t('cs_upload')}
              </label>
              {qrCode && <button onClick={() => setQrCode('')} style={btn('transparent', '#E24B4A')}>{t('cs_remove')}</button>}
            </div>
          </div>
          <div><label style={lbl}>{t('cs_remark')}</label><textarea value={remark} onChange={e => setRemark(e.target.value)} rows={3} style={{ ...input, resize: 'vertical' }} /></div>
          {err && <div style={{ fontSize: 12, color: '#E24B4A', background: '#FCEBEB', padding: '8px 12px', borderRadius: 'var(--radius-md)' }}>{err}</div>}
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
            <button onClick={onClose} style={btn('var(--c-gray-100)', 'var(--text-secondary)')}>{t('cancel')}</button>
            <button onClick={save} disabled={saving} style={btn('var(--accent)')}>{saving ? t('login_wait') : t('save')}</button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Company settings (basic info + bank accounts) ───────────────────────────
export default function CompanySettings({ companyId, companyName, onBack, onChanged }: {
  companyId: string; companyName: string; onBack: () => void; onChanged: () => void;
}) {
  const { t } = useTranslation();
  const [detail, setDetail] = useState<CompanyDetail | null>(null);
  const [err, setErr] = useState(''); const [msg, setMsg] = useState('');
  const [savingBasic, setSavingBasic] = useState(false);
  const [bankEditing, setBankEditing] = useState<BankAccount | null>(null);
  const [showBank, setShowBank] = useState(false);

  // basic-info fields
  const [f, setF] = useState({
    name: companyName, shortName: '', taxId: '', registerDate: '', vatRegistered: false,
    address: '', email: '', contactPerson: '', contactPhone: '', website: '', logo: '', stamp: '',
  });
  const set = (k: string, v: any) => setF(prev => ({ ...prev, [k]: v }));

  const load = async () => {
    try {
      const d: CompanyDetail = await api.adminGetCompany(companyId);
      setDetail(d);
      setF({
        name: d.name || '', shortName: d.short_name || '', taxId: d.tax_id || '', registerDate: d.register_date || '',
        vatRegistered: d.vat_registered === 1, address: d.address || '', email: d.email || '',
        contactPerson: d.contact_person || '', contactPhone: d.contact_phone || '', website: d.website || '',
        logo: d.logo || '', stamp: d.stamp || '',
      });
    } catch (e: any) { setErr(e.message); }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line

  const saveBasic = async () => {
    setSavingBasic(true); setErr(''); setMsg('');
    try {
      await api.adminUpdateCompany(companyId, {
        name: f.name, shortName: f.shortName, taxId: f.taxId, registerDate: f.registerDate,
        vatRegistered: f.vatRegistered, address: f.address, email: f.email,
        contactPerson: f.contactPerson, contactPhone: f.contactPhone, website: f.website,
        logo: f.logo, stamp: f.stamp,
      });
      setMsg(t('cs_saved')); onChanged();
      setTimeout(() => setMsg(''), 2000);
    } catch (e: any) { setErr(e.message); } finally { setSavingBasic(false); }
  };

  const deleteBank = async (acc: BankAccount) => {
    if (!window.confirm(t('cs_confirm_delete_bank'))) return;
    await api.adminDeleteBankAccount(companyId, acc.id).catch((e: any) => setErr(e.message));
    load();
  };

  const imageField = (label: string, key: 'logo' | 'stamp') => (
    <div>
      <label style={lbl}>{label}</label>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        {(f as any)[key]
          ? <img src={(f as any)[key]} alt={label} style={{ width: 64, height: 64, objectFit: 'contain', borderRadius: 8, border: '1px solid var(--border)', background: '#fff' }} />
          : <div style={{ width: 64, height: 64, borderRadius: 8, border: '1px dashed var(--border-strong)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-tertiary)' }}><i className="ti ti-photo" style={{ fontSize: 20 }} /></div>}
        <label style={{ ...btn('var(--c-gray-100)', 'var(--text-secondary)'), display: 'inline-block' }}>
          <input type="file" accept="image/*" style={{ display: 'none' }} onChange={e => { const file = e.target.files?.[0]; if (file) readImage(file, d => set(key, d)); }} />
          {t('cs_upload')}
        </label>
        {(f as any)[key] && <button onClick={() => set(key, '')} style={btn('transparent', '#E24B4A')}>{t('cs_remove')}</button>}
      </div>
    </div>
  );

  const field = (label: string, key: keyof typeof f, type = 'text') => (
    <div>
      <label style={lbl}>{label}</label>
      <input type={type} value={(f as any)[key]} onChange={e => set(key as string, e.target.value)} style={input} />
    </div>
  );

  return (
    <div style={{ height: '100%', overflowY: 'auto', padding: 20 }}>
      <div style={{ maxWidth: 860, margin: '0 auto' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
          <button onClick={onBack} style={{ ...btn('var(--c-gray-100)', 'var(--text-secondary)'), padding: '6px 12px' }}>
            <i className="ti ti-arrow-left" style={{ fontSize: 13, marginRight: 6 }} aria-hidden />{t('cs_companies_back')}
          </button>
          <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--text-primary)' }}>{f.name || companyName}</div>
        </div>

        {err && <div style={{ fontSize: 12, color: '#E24B4A', background: '#FCEBEB', padding: '8px 12px', borderRadius: 'var(--radius-md)', marginBottom: 12 }}>{err}</div>}

        {/* Basic info */}
        <div style={card}>
          <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 14 }}>{t('cs_basic_info')}</div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            {field(t('company_name'), 'name')}
            {field(t('cs_short_name'), 'shortName')}
            {field(t('cs_tax_id'), 'taxId')}
            {field(t('cs_register_date'), 'registerDate', 'date')}
            {field(t('cs_email'), 'email')}
            {field(t('cs_website'), 'website')}
            {field(t('cs_contact_person'), 'contactPerson')}
            {field(t('cs_contact_phone'), 'contactPhone')}
          </div>
          <div style={{ marginTop: 12 }}>{field(t('cs_address'), 'address')}</div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12, marginTop: 12, alignItems: 'end' }}>
            <div>
              <label style={lbl}>{t('cs_vat')}</label>
              <div style={{ display: 'flex', gap: 8 }}>
                {[false, true].map(v => (
                  <button key={String(v)} onClick={() => set('vatRegistered', v)} style={{ flex: 1, padding: '8px', borderRadius: 'var(--radius-md)', border: '1px solid', borderColor: f.vatRegistered === v ? 'var(--accent)' : 'var(--border-strong)', background: f.vatRegistered === v ? 'var(--accent)' : 'transparent', color: f.vatRegistered === v ? '#fff' : 'var(--text-secondary)', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>{v ? 'Y' : 'N'}</button>
                ))}
              </div>
            </div>
            {imageField(t('cs_logo'), 'logo')}
            {imageField(t('cs_stamp'), 'stamp')}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 12, marginTop: 16 }}>
            {msg && <span style={{ fontSize: 12, color: '#1D9E75' }}><i className="ti ti-check" style={{ marginRight: 4 }} />{msg}</span>}
            <button onClick={saveBasic} disabled={savingBasic} style={btn('var(--accent)')}>{savingBasic ? t('login_wait') : t('save')}</button>
          </div>
        </div>

        {/* Bank accounts */}
        <div style={card}>
          <div style={{ display: 'flex', alignItems: 'center', marginBottom: 14 }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--text-primary)' }}>{t('cs_bank_accounts')}</div>
            <div style={{ flex: 1 }} />
            <button onClick={() => { setBankEditing(null); setShowBank(true); }} style={btn('var(--accent)')}>
              <i className="ti ti-plus" style={{ fontSize: 13, marginRight: 6 }} aria-hidden />{t('cs_add_bank')}
            </button>
          </div>
          {(detail?.bankAccounts || []).length === 0 ? (
            <div style={{ fontSize: 13, color: 'var(--text-tertiary)', padding: '12px 0' }}>{t('cs_no_bank')}</div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {detail!.bankAccounts.map(a => (
                <div key={a.id} style={{ display: 'flex', alignItems: 'center', gap: 14, border: '1px solid var(--border)', borderRadius: 'var(--radius-md)', padding: 12 }}>
                  {a.qr_code
                    ? <img src={a.qr_code} alt="QR" style={{ width: 48, height: 48, objectFit: 'contain', borderRadius: 6, border: '1px solid var(--border)', background: '#fff' }} />
                    : <div style={{ width: 48, height: 48, borderRadius: 6, background: 'var(--bg-active)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-tertiary)' }}><i className="ti ti-building-bank" /></div>}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)' }}>
                      {a.account_name || '—'}
                      <span style={{ marginLeft: 8, fontSize: 10, fontWeight: 600, padding: '1px 8px', borderRadius: 10, background: 'var(--bg-active)', color: 'var(--text-secondary)' }}>
                        {t(a.account_type === 'corporate' ? 'cs_corporate' : 'cs_personal')}
                      </span>
                    </div>
                    <div style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>{a.account_number}{a.swift_code ? ' · ' + a.swift_code : ''}</div>
                  </div>
                  <button onClick={() => { setBankEditing(a); setShowBank(true); }} title={t('edit')} style={{ border: 'none', background: 'none', color: 'var(--text-secondary)', cursor: 'pointer' }}><i className="ti ti-pencil" /></button>
                  <button onClick={() => deleteBank(a)} title={t('delete')} style={{ border: 'none', background: 'none', color: '#E24B4A', cursor: 'pointer' }}><i className="ti ti-trash" /></button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {showBank && <BankForm companyId={companyId} account={bankEditing} onClose={() => setShowBank(false)} onSaved={load} />}
    </div>
  );
}
